import { useState } from "react";
import { accountService } from "@miplatform/api";
import {
  DOCUMENT_TYPES,
  ENTITY_TYPES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { useGlobalManagerApproval } from "../../../../../components/GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";

export const useAddPurchaseOrderModalViewModel = ({
  homeId,
  selectedPurchaseOrder,
  onPurchaseOrdersRefresh,
  onCreate,
  onClose,
}) => {
  const { loading, uploadDocument, getDocumentCategories } =
    useDocumentsViewModel();
  const [file, setFile] = useState(null);
  const [documentCategory, setDocumentCategory] = useState(null);
  const stateManager = useComponentState();
  const isLoading = loading || stateManager.isBusy();
  const { executeApiRequest } = useGlobalManagerApproval();

  const handleFileChange = (files) => {
    if (files?.length > 0) {
      setFile(files[0]);
    }
  };

  const resetFile = () => {
    setFile(null);
  };

  const getDocumentCategory = async () => {
    const categories = await getDocumentCategories(ENTITY_TYPES.PURCHASE_ORDER);
    if (categories?.length > 0) {
      setDocumentCategory(categories[0]);
    }
  };

  const createPurchaseOrder = (data) => {
    stateManager.run(async () => {
      const options = await accountService.billingCommitments.getCreateOptions({
        data: { homeId, ...data },
      });

      const callback = async (response) => {
        if (response.status === HTTP_STATUSES.OK) {
          toastService.success({
            text: `New payment information ${data.name} was created.`,
          });

          if (file) {
            const uploadResponse = await uploadDocument({
              file,
              documentType: DOCUMENT_TYPES.UNKNOWN,
              category: documentCategory,
              externalReferenceId: externalReferenceId.getPurchaseOrderRefId({
                purchaseOrderId: response.data.id,
              }),
            });

            if (uploadResponse?.id) {
              onCreate(response.data);
              toastService.success({ text: "File uploaded successfully." });
            }
          }

          onPurchaseOrdersRefresh();
          onClose();
          resetFile();
        }
      };

      await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Creating payment information requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });
    });
  };

  const updatePurchaseOrder = (data) => {
    stateManager.run(async () => {
      const options = await accountService.billingCommitments.getUpdateOptions({
        id: selectedPurchaseOrder?.id,
        data: { homeId, modifiedOn: selectedPurchaseOrder.modifiedOn, ...data },
      });

      const callback = async (response) => {
        if (response.status === HTTP_STATUSES.OK) {
          toastService.success({
            text: `Payment information ${data.name} was updated.`,
          });

          if (file) {
            const uploadResponse = await uploadDocument({
              file,
              documentType: DOCUMENT_TYPES.UNKNOWN,
              category: documentCategory,
              externalReferenceId: externalReferenceId.getPurchaseOrderRefId({
                purchaseOrderId: response.data.id,
              }),
            });

            if (uploadResponse?.id) {
              toastService.success({ text: "File uploaded successfully." });
            }
          }

          onPurchaseOrdersRefresh();
          onClose();
          resetFile();
        }
      };

      await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Updating payment information requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });
    });
  };

  return {
    documentCategory,
    isLoading,
    file,
    getDocumentCategory,
    createPurchaseOrder,
    updatePurchaseOrder,
    handleFileChange,
    resetFile,
  };
};
